import React from 'react';
import Field from './Field';
import { cn } from 'lib/utils';

export interface ToggleOption {
    value: string;
    label: string;
}

export interface ToggleGroupProps {
    label?: React.ReactNode;
    hint?: React.ReactNode;
    error?: string;
    required?: boolean;
    options: ToggleOption[];
    /** 다중 선택이면 string[], 단일 선택이면 string */
    value: string[] | string;
    multiple?: boolean;
    disabled?: boolean;
    onChange: (next: any) => void;
    className?: string;
}

/**
 * 선택지가 2~4개인 값(공종, 구분 등)을 체크박스/라디오 대신 토글 버튼으로 고른다.
 * 입력 높이(36px)와 테두리를 다른 필드와 맞춰 한 줄에 섞여도 어색하지 않게 한다.
 */
const ToggleGroup: React.FC<ToggleGroupProps> = ({
    label,
    hint,
    error,
    required,
    options,
    value,
    multiple = false,
    disabled = false,
    onChange,
    className = '',
}) => {
    const selected = Array.isArray(value) ? value : value ? [value] : [];

    const toggle = (optionValue: string) => {
        if (disabled) return;
        if (multiple) {
            const next = selected.includes(optionValue)
                ? selected.filter((v) => v !== optionValue)
                : [...selected, optionValue];
            onChange(next);
            return;
        }
        onChange(selected.includes(optionValue) ? '' : optionValue);
    };

    return (
        <Field label={label} hint={hint} error={error} required={required} className={className}>
            <div className="flex flex-wrap gap-2" role="group">
                {options.map((option) => {
                    const active = selected.includes(option.value);
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role={multiple ? 'checkbox' : 'radio'}
                            aria-checked={active}
                            disabled={disabled}
                            onClick={() => toggle(option.value)}
                            className={cn(
                                'inline-flex h-9 items-center rounded border px-3.5 text-base font-medium transition-colors duration-fast',
                                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                active
                                    ? 'border-primary bg-navy-50 text-primary'
                                    : 'border-input bg-background text-neutral-600 hover:border-neutral-300 hover:text-foreground',
                                disabled && 'cursor-not-allowed opacity-60'
                            )}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </Field>
    );
};

export default ToggleGroup;
